// Angular imports
import { Component, OnInit, Input, ViewChild, ElementRef, ChangeDetectionStrategy, Output, EventEmitter } from '@angular/core';
import { PercentPipe } from '@angular/common';

// Cell base class
import { CellComponent } from '../cell.component';

// Import components
import { MultiLineTextInputComponent } from '../../../components/multi-line-text-input/multi-line-text-input.component';

@Component({
    selector: 'multi-line-text-input-cell',
    templateUrl: './multi-line-text-input-cell.component.html',
    styleUrls: ['./multi-line-text-input-cell.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [PercentPipe],
    standalone: false
})
export class MultiLineTextInputCellComponent extends CellComponent<string> implements OnInit {
    @ViewChild(MultiLineTextInputComponent) public _inputElement: MultiLineTextInputComponent;

    @Output() public input = new EventEmitter<string>();

    public ngOnInit() {
        super.ngOnInit();
    }

    /**
     * Place the cursor on this cell's content
     * @param shallow Whether or not to focus shallowly
     * @returns void
     */
    public focus(shallow: boolean = true): void {
        // Set whether we have shallow focus or not
        if (this.hasShallowFocus === null)
            this.hasShallowFocus = shallow;

        // Focus the input
        this._inputElement.focus();

        // Select all content
        if (this.selectOnFocus)
            this.selectContent();

        // Trigger focus event
        this.onFocus.emit();
    }

    /**
     * Remove focus from this cell's content. This does not remove the selection
     * @returns void
     */
    public blur(): void {
        // Blur the input
        this._inputElement.blur();

        // Trigger blur event
        this.onBlur.emit();
    }

    /**
     * Select the cell.
     * @returns void
     */
     public select(setOrigin: boolean = false): void { 
        // If already selected, return
        if (this.isSelected) return;

        // Emit selection changed event
        // console.log('EMIT', setOrigin, {
        //     origin: setOrigin ? this.selection.origin ?? this.selection.current : undefined,
        //     current: { row: this.rowIndex, column: this.column.index } 
        // });
        
        this.selectionChange.emit({
            origin: setOrigin ? this.selection.origin ?? this.selection.current : undefined,
            current: { row: this.rowIndex, column: this.column.index } 
        });
    }

    /**
     * Select all of the cell's content.
     * @returns void
     */
    public selectContent(): void {
        // Select the cell's content
        this._inputElement.select();
    }

    /* --- Event handlers --- */

    // Handle value change event
    public _handleValueChange(value: string) {
        this.valueChange.emit(value);
    }

    // Handle focus event
    public _handleFocus(event: MouseEvent) {
        // Check if this cell is currently not selected
        if (!this.isSelected) {
            // Focus and then blur (to lose other focus)
            this._inputElement.focus();
            
            // Blur the content
            this.blur();

            // Select the cell
            return this.select(event.shiftKey);
        }

        // The cell is selected, deep focus
        this.focus(false);
    }

    // Handle blur event
    public _handleBlur(event: Event) {
        // Clear has shallow focus
        this.hasShallowFocus = null;
    }

    // Handle tabs, enters and arrow keys
    public _handleKeydown(event: KeyboardEvent) {
        const notKey = ['Escape', 'Tab'];
        const notKeyWhenShallowFocus = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if(!notKey.includes(event.key) && !(notKeyWhenShallowFocus.includes(event.key) && this.hasShallowFocus)){
            return event.stopPropagation();
        }

        // if (event.key !== 'Escape' && event.key !== 'Tab' && !(event.key === 'ArrowLeft' && this.hasShallowFocus) && !(event.key === 'ArrowRight' && this.hasShallowFocus) && !(event.key === 'ArrowUp' && this.hasShallowFocus) && !(event.key === 'ArrowDown' && this.hasShallowFocus))
        //     return event.stopPropagation();

        // Prevent default behavior
        event.preventDefault();
        event.stopPropagation();

        if (event.key === 'ArrowUp' && this.hasShallowFocus || (event.key === 'Tab' && event.shiftKey))
            this.selectAbove.emit();
        if (event.key === 'ArrowDown' && this.hasShallowFocus || (event.key === 'Tab' && !event.shiftKey))
            this.selectBelow.emit();

        // Remove focus here
        this.blur();
    }

    public _handleInput(input: string): void {
        this.input.emit(input);
    }

    public _purgeEvent(event: Event) {
        event.stopPropagation();
    }
}
